import { useEffect, type RefObject } from "react";
import { type AddressVerifyIssue, firstIssueTarget } from "../utils/validation";

const ATTENTION_CLASS = "pixi-field-attention";

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

/**
 * When address-verification issues appear (after a Continue click), scroll the
 * first failed field to the centre of the scroll container, focus its control,
 * and play a brief shake to draw the eye. Fields are located by their existing
 * `data-testid` (`${form}-${field}`) within `containerRef`.
 *
 * Keyed on the issues array identity: `verifyThenContinue` writes a fresh array
 * on every Continue, so a repeat failure re-runs (and restarts the animation),
 * while unrelated re-renders don't. A no-op for an empty/absent list.
 */
export const useFocusFirstVerifyIssue = (
  containerRef: RefObject<HTMLElement>,
  issues: AddressVerifyIssue[] | undefined,
): void => {
  useEffect(() => {
    if (!issues || issues.length === 0) return;
    const container = containerRef.current;
    if (!container) return;

    const target = firstIssueTarget(issues);
    if (!target) return;

    const node = container.querySelector<HTMLElement>(
      `[data-testid="${target.form}-${target.field}"]`,
    );
    if (!node) return;

    const reduced = prefersReducedMotion();
    // Optional-call: jsdom (tests) doesn't implement scrollIntoView.
    node.scrollIntoView?.({
      behavior: reduced ? "auto" : "smooth",
      block: "center",
    });

    // Focus the underlying control (the testid sits on the field wrapper for
    // inputs/dropdowns). preventScroll so focus doesn't fight the smooth scroll.
    const focusable = node.matches("input, textarea, select")
      ? node
      : node.querySelector<HTMLElement>("input, textarea, select, [tabindex]");
    focusable?.focus?.({ preventScroll: true });

    if (reduced) return;
    node.classList.remove(ATTENTION_CLASS);
    void node.offsetWidth; // reflow so re-adding restarts the animation
    node.classList.add(ATTENTION_CLASS);
    node.addEventListener(
      "animationend",
      () => node.classList.remove(ATTENTION_CLASS),
      { once: true },
    );
  }, [issues, containerRef]);
};
